import React from "react"
import { MainButton } from "@/Components/MainButton"
import { useAppDispatch, useAppSelector } from "@/store/hooks"
import {
  setState,
  setModalData,
  setActiveTrade,
  setTradeData,
} from "@/store/globalSlice"
import { getUserCreds, isMobile } from "@/utils/utils"

/**
 * The "Create Trade" call-to-action. It used to sit with the turn buttons in
 * the middle of the board; it now lives in the left sidebar right above the
 * bank, so starting a trade and reading the trades list are on opposite sides
 * of the board rather than fighting the roll/build buttons for the same slot.
 *
 * Trading is turn-independent, so this is visible to everyone at all times —
 * only a bankrupt player or a finished game takes it away.
 */
const CreateTradeButton: React.FC = () => {
  const dispatch = useAppDispatch()
  const situationPlayers = useAppSelector(
    (state) => state.global.myGame?.situation?.players
  )
  const gameOver = useAppSelector(
    (state) => state.global.myGame?.situation?.gameOver
  )

  const myPlayer = situationPlayers?.find((p) => p.id === getUserCreds().email)

  const startTrade = () => {
    const myCurrentOrder = myPlayer?.order
    // Starting a fresh offer takes any drawer that is already open down with
    // it. The draft below has no offered player yet, so a drawer left flagged
    // open would render nothing while still dimming the board into trade mode —
    // and the drawer's own close button is the only thing that clears that flag.
    dispatch(setState({ viewedTradeId: null }))
    dispatch(setModalData({ trade: { visible: false } }))
    dispatch(
      setActiveTrade({
        givenProperties: [],
        moneyGiven: 0,
        moneyTaken: 0,
        offeringPlayerOrder: myCurrentOrder as PlayerOrder,
        offeredPlayerOrder: null as any,
        takenProperties: [],
      })
    )
    dispatch(setTradeData({ status: "createing-offer" }))
    // Open the small player chooser first; selecting a player opens the trade
    // modal itself.
    dispatch(setState({ tempTradeData: { status: "choosing-player" } }))
  }

  if (gameOver || myPlayer?.isBankrupt) return null

  return (
    // Plain MainButton skin, same as the roll/build buttons — only the contents
    // are richer. Blue is the colour the trade button already carried in the
    // board's button row, and it reads over every board background (gold got
    // swallowed by the warm ones). Inset from the screen edge, mirroring the
    // trades panel's ml-4 on the right sidebar.
    //
    // !h-16 rather than the base size's h-14: this and the Last Auction Results
    // button above it are one stack of two full-width keys, and half a rem of
    // difference between them reads as a slip rather than a distinction.
    <MainButton
      baseColor="#4f8fc9"
      size="base"
      onClick={startTrade}
      className="group ml-4 w-[calc(100%-1rem)] !h-16 !px-4"
      data-testid="game.trade-btn"
      title="Create a new trade offer"
      aria-label="Create trade"
      disabled={false}
    >
      <div className="flex w-full flex-row items-center gap-2.5">
        <span className="flex h-9 w-9 sm:h-8 sm:w-8 shrink-0 items-center justify-center rounded-full bg-[#12334f] ring-1 ring-black/40 shadow-[inset_0_1px_0_rgba(255,255,255,0.18)]">
          <i className="fa-solid fa-handshake text-[#9fd3ff] text-base transition-transform duration-150 group-hover:scale-110"></i>
        </span>
        <span className="min-w-0 flex-1 text-left">
          {/* rem sizes throughout — the root font-size shrinks with the window
              (index.css), so literal px would leave the caption as large as the
              title on narrower screens. */}
          <span className="block font-poppins text-lg sm:text-base font-extrabold uppercase leading-none tracking-wider text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.45)]">
            Create Trade
          </span>
          {/* Caption is dropped on mobile — the row is tight there and the
              title alone already says what the button does. */}
          {!isMobile() && (
            <span className="mt-1 block font-poppins text-[0.65rem] font-semibold uppercase leading-none tracking-wide text-white/75">
              Offer a deal to any player
            </span>
          )}
        </span>
        <i className="fa-solid fa-chevron-right text-white/80 text-xs transition-transform duration-150 group-hover:translate-x-0.5"></i>
      </div>
    </MainButton>
  )
}

export default React.memo(CreateTradeButton)
